"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentPermissionsData = void 0;
exports.userPermissionTypeFromRoles = userPermissionTypeFromRoles;
exports.userPermissionType = userPermissionType;
exports.anonymousAccessLevel = anonymousAccessLevel;
exports.isPrivateDocument = isPrivateDocument;
exports.isPublicDocument = isPublicDocument;
const constants_1 = require("@buildwithflux/constants");
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const enterprisePermissions_1 = require("./enterprisePermissions");
const organizationPermissions_1 = require("./organizationPermissions");
const permissionType_1 = require("./permissionType");
/**
 * Key of the document permissions map - roughly, the principals that can have permissions on a document
 *
 * @todo In the near future, we may need to add organizations to this data, e.g. to allow cross-organization
 *       sharing within enterprises
 */
const DocumentPermissionsKey = zod_1.z.union([identifier_1.UserUid, zod_1.z.literal(constants_1.ANON_ROLE_UID)]);
exports.DocumentPermissionsData = zod_1.z.object({
    user_uid: zod_1.z.union([zod_1.z.string(), zod_1.z.literal(constants_1.ANON_ROLE_UID)]),
    permission_type: zod_1.z.nativeEnum(permissionType_1.PermissionType),
    created_at: zod_1.z.number(),
    updated_at: zod_1.z.number(),
});
/**
 * Recommend using userPermissionType() instead, unless you're doing something low-level
 *
 * @see Document.setUserPermissions
 */
function userPermissionTypeFromRoles(roles, userUid, hasEditPrivateProjectEntitlement) {
    let userPermission = roles[userUid]?.permission_type;
    if (isPrivateDocument(roles) && !hasEditPrivateProjectEntitlement) {
        if (userPermission === permissionType_1.PermissionType.edit) {
            userPermission = permissionType_1.PermissionType.view;
        }
    }
    return userPermission ?? anonymousAccessLevel(roles);
}
/**
 * Recommend using userPermissionType() instead, unless you're doing something low-level
 */
function userPermissionTypeFromOrganizations(documentOrganizationOwnerUid, documentOrganizationMemberPermissionType, organizationMemberships) {
    if (!documentOrganizationOwnerUid) {
        return permissionType_1.PermissionType.none;
    }
    const membership = organizationMemberships.find((membership) => membership.organizationUid === documentOrganizationOwnerUid);
    if (!membership) {
        return permissionType_1.PermissionType.none;
    }
    if (membership.role === "owner") {
        return permissionType_1.PermissionType.edit;
    }
    else if (membership.role === "member") {
        return documentOrganizationMemberPermissionType;
    }
    else {
        throw new Error(`Unknown membership type: ${membership.role}`);
    }
}
/**
 * Recommend using userPermissionType() instead, unless you're doing something low-level
 */
function userPermissionTypeFromEnterprises(documentEnterpriseOwnerUid, documentEnterpriseMemberPermissionType, organizationMemberships) {
    if (!documentEnterpriseOwnerUid) {
        return permissionType_1.PermissionType.none;
    }
    const membership = organizationMemberships.find((membership) => membership.enterpriseUid === documentEnterpriseOwnerUid);
    if (!membership) {
        return permissionType_1.PermissionType.none;
    }
    if (membership.role === "owner") {
        return permissionType_1.PermissionType.edit;
    }
    else if (membership.role === "member") {
        return documentEnterpriseMemberPermissionType;
    }
    else {
        throw new Error(`Unknown membership type: ${membership.role}`);
    }
}
/**
 * Returns the highest permission granted among the contributing authorization types.
 *
 * hasEditPrivateProjectEntitlement, coming from the user's paid plan or trial
 * or legacy plan, modifies how owner and role permissions are applied to
 * private documents. Effectively, when hasEditPrivateProjectEntitlement is false
 * and there is no anon permission, owner and individual role permissions are
 * clamped to view-only, while anon role permission still applies, so that
 * publicly editable projects are still editable.
 *
 * @see documentPermissions.test.ts for examples.
 *
 * QUESTION: what about commenting-only permission? do we want to clamp that also?
 */
function userPermissionType(userUidToCheck, documentOwnerUid, roles, documentOrganizationOwnerUid, documentOrganizationMemberPermissionType, documentEnterpriseOwnerUid, documentEnterpriseMemberPermissionType, organizationMemberships, hasEditPrivateProjectEntitlement) {
    const ownerPermission = ownerPermissionType(documentOwnerUid, userUidToCheck, roles, hasEditPrivateProjectEntitlement);
    const rolePermission = userPermissionTypeFromRoles(roles, userUidToCheck, hasEditPrivateProjectEntitlement);
    const orgPermission = userPermissionTypeFromOrganizations(documentOrganizationOwnerUid, documentOrganizationMemberPermissionType ?? organizationPermissions_1.PermissionTypeDefaultForOrganizationMembers, organizationMemberships);
    const enterprisePermission = userPermissionTypeFromEnterprises(documentEnterpriseOwnerUid, documentEnterpriseMemberPermissionType ?? enterprisePermissions_1.PermissionTypeDefaultForEnterpriseMembers, organizationMemberships);
    return (0, permissionType_1.effectivePermissionType)(ownerPermission, rolePermission, orgPermission, enterprisePermission);
}
/**
 * @see userPermissionType
 */
function ownerPermissionType(documentOwnerUid, userUidToCheck, roles, hasEditPrivateProjectEntitlement) {
    if (documentOwnerUid === userUidToCheck) {
        if (isPrivateDocument(roles)) {
            if (hasEditPrivateProjectEntitlement) {
                return permissionType_1.PermissionType.edit;
            }
            return permissionType_1.PermissionType.view;
        }
        return permissionType_1.PermissionType.edit;
    }
    return permissionType_1.PermissionType.none;
}
/**
 * Check what level of access the anonymous user has.
 *
 * @see Document.setUserPermissions
 */
function anonymousAccessLevel(roles) {
    return roles[constants_1.ANON_ROLE_UID]?.permission_type ?? permissionType_1.PermissionType.none;
}
/**
 * Defines higher-level concept of "private" document.
 *
 * @see Document.setUserPermissions
 */
function isPrivateDocument(roles) {
    return anonymousAccessLevel(roles) === permissionType_1.PermissionType.none;
}
/**
 * Defines higher-level concept of "public" document.
 *
 * @see Document.setUserPermissions
 */
function isPublicDocument(roles) {
    return anonymousAccessLevel(roles) !== permissionType_1.PermissionType.none;
}
//# sourceMappingURL=documentPermissions.js.map